/**
 * OutcomeResultView — the stat-card grid + outcome-bucket-list wrapper every
 * post-commit fan-out result view renders (ReserveResultSection,
 * ReleaseResultSection in reserve-preliminary-result-view.tsx, and
 * RemoveResultView in remove-rejected-units-dialog.tsx all hand-rolled the
 * same composition before this extraction). Callers still build their own
 * `OutcomeResultBucket` list as `children`, since each bucket's entry type
 * and `renderEntry` differ per action — only the shared stat grid and
 * wrapping structure lives here.
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { StatCard } from '@ooh/ui/components/stat-card';

export interface OutcomeResultStatConfig {
  icon: LucideIcon;
  tone: 'success' | 'warning' | 'destructive' | 'neutral';
  label: string;
  value: number;
  testId: string;
}

export interface OutcomeResultViewProps {
  testId?: string;
  title?: string;
  /** Tailwind grid-column class for the stat row, e.g. "grid-cols-2" or "grid-cols-3". */
  className: string;
  stats: OutcomeResultStatConfig[];
  children: ReactNode;
}

export function OutcomeResultView({
  testId,
  title,
  className,
  stats,
  children,
}: OutcomeResultViewProps) {
  return (
    <div className="space-y-3" data-testid={testId}>
      {title ? <h3 className="text-sm font-semibold">{title}</h3> : null}
      <div className={`grid ${className} gap-3`}>
        {stats.map((stat) => (
          <StatCard
            key={stat.testId}
            icon={stat.icon}
            tone={stat.tone}
            label={stat.label}
            value={stat.value}
            loading={false}
            testId={stat.testId}
          />
        ))}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
